import * as React from "react"
import { motion } from "framer-motion"
import { cn } from "../../lib/utils"

interface TimelineItemProps {
  date: string
  title: string
  company?: string
  description: string
  type: "education" | "internship" | "job" | "freelance"
  isLast?: boolean
}

const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(
  ({ date, title, company, description, type, isLast = false }, ref) => {
    const getTypeAccent = () => {
      switch (type) {
        case "education":
          return "border-l-blue-500/60"
        case "internship":
          return "border-l-yellow-500/60"
        case "job":
          return "border-l-purple-500/60"
        case "freelance":
          return "border-l-emerald-500/60"
        default:
          return "border-l-gray-500/60"
      }
    }

    return (
      <div ref={ref} className="relative flex items-center group">
        {/* Espaço reservado para a linha/lua central */}
        <div className="w-8 flex-shrink-0" />

        {/* Content */}
        <div className={cn("ml-6 flex-1", !isLast && "pb-8")}>
          <div className={cn(
            "glass p-6 rounded-xl border border-white/10 border-l-4 hover:border-white/20 transition-all duration-300 group-hover:scale-105",
            getTypeAccent()
          )}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-2">
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              <span className="text-sm text-yellow-400 font-medium">{date}</span>
            </div>
            {company && (
              <p className="text-purple-300 font-medium mb-2">{company}</p>
            )}
            <p className="text-gray-300 text-sm leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    )
  }
)
TimelineItem.displayName = "TimelineItem"

interface TimelineProps {
  children: React.ReactNode
  className?: string
}

const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("relative space-y-0", className)}
        {...props}
      >
        {/* Linha vertical contínua */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-white/30 via-white/20 to-transparent -translate-x-1/2" />

        {/* Luazinha fixa na tela, sempre ao lado do item atual */}
        <div className="sticky top-[50vh] h-0 z-20 pointer-events-none">
          <div className="absolute left-4 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
            <div className="absolute w-10 h-10 rounded-full bg-slate-200/30 blur-xl" />
            {/* Esfera com sombreamento radial + crateras (ao invés de um
                ícone plano) para parecer uma lua de verdade. Sem rotação
                contínua: a Lua é travada por maré, então da Terra a gente
                nunca vê a face "girando" — só um leve flutuar. */}
            <motion.div
              className="relative w-8 h-8 rounded-full ring-1 ring-black/30 shadow-[0_0_16px_rgba(226,232,240,0.65)]"
              style={{
                background: `
                  radial-gradient(circle at 44% 42%, rgba(15,13,10,0.35) 0%, rgba(15,13,10,0.35) 7%, transparent 17%),
                  radial-gradient(circle at 63% 60%, rgba(15,13,10,0.28) 0%, rgba(15,13,10,0.28) 6%, transparent 15%),
                  radial-gradient(circle at 30% 62%, rgba(15,13,10,0.22) 0%, rgba(15,13,10,0.22) 5%, transparent 13%),
                  radial-gradient(circle at 56% 24%, rgba(15,13,10,0.18) 0%, rgba(15,13,10,0.18) 4%, transparent 11%),
                  radial-gradient(circle at 20% 30%, rgba(15,13,10,0.15) 0%, transparent 10%),
                  radial-gradient(circle at 34% 30%, #fbfbf6 0%, #e6e1d2 14%, #c7c0a9 32%, #948d76 55%, #5f5946 76%, #2c2819 100%)
                `,
              }}
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>

        {children}
      </div>
    )
  }
)
Timeline.displayName = "Timeline"

export { Timeline, TimelineItem }
